import { isAxiosError } from "axios"

import type { PersonalPolicyPush, PersonalSpacesSource } from "@/api/admin-api"

/** The three features that govern a personal source, and the source each one governs. */
const PERSONAL_SOURCE_FEATURES: Record<string, PersonalSpacesSource> = {
  personal_source_onedrive: "onedrive",
  personal_source_mail: "mail",
  personal_source_chats: "chats",
}

export const PERSONAL_HISTORY_WINDOW_MIN_DAYS = 1
export const PERSONAL_HISTORY_WINDOW_MAX_DAYS = 365
export const PERSONAL_HISTORY_WINDOW_DEFAULT_DAYS = 30

export function personalSourceOfFeature(featureId: string | null | undefined) {
  return featureId ? (PERSONAL_SOURCE_FEATURES[featureId] ?? null) : null
}

export function isPersonalSourceFeature(featureId: string | null | undefined): boolean {
  return personalSourceOfFeature(featureId) !== null
}

/** A whole number of days inside the allowed range, or null. */
export function parseHistoryWindowDays(value: string): number | null {
  const text = value.trim()
  if (!/^\d+$/.test(text)) return null
  const days = Number(text)
  return days >= PERSONAL_HISTORY_WINDOW_MIN_DAYS && days <= PERSONAL_HISTORY_WINDOW_MAX_DAYS
    ? days
    : null
}

/** Raising the window indexes older items for every user of the source (criterion 20). */
export function isWidening(currentDays: number, requestedDays: number): boolean {
  return requestedDays > currentDays
}

export type WidenRequirement = { currentDays: number | null; requestedDays: number | null }

/**
 * The server's answer when a window increase arrives without `widen_confirmed`
 * (409 `widen_confirmation_required`), or null for any other failure. The client
 * normally asks first; this catches a window that changed under the admin.
 */
export function widenRequirementFromError(error: unknown): WidenRequirement | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null
  const body = error.response.data as
    | { code?: unknown; current_days?: unknown; requested_days?: unknown }
    | undefined
  if (body?.code !== "widen_confirmation_required") return null
  const days = (value: unknown) => (typeof value === "number" ? value : null)
  return { currentDays: days(body.current_days), requestedDays: days(body.requested_days) }
}

/** True when FILTH is still enforcing the previous policy (anything but applied). */
export function isPushPending(push: PersonalPolicyPush | undefined): boolean {
  return push !== undefined && push.status !== "applied"
}
